import type { MasterProduct } from 'apis/master-products';
import { useGetMasterProductsApi } from 'apis/master-products';
import { useGetAttributeDictionaryApi } from 'apis/product-attributes';
import type {
  QuotationSupplierProduct,
  QuotationSupplierProductOption,
  RestaurantInquiryItem,
} from 'apis/restaurant-inquiries';
import { useSearchQuotationSupplierProductsApi } from 'apis/restaurant-inquiries';
import { Badge, Box, Button, ChakraAsyncSelect, Flex, Grid, Spinner, Text } from 'design-system';
import { VariantFormModal } from 'pages/inquiry-processor/components/master-product-families/variant-form-modal';
import { ProductPriceTag } from 'pages/inquiry-processor/components/quote-matcher/price-ladder';
import { SimilarVariantCards } from 'pages/inquiry-processor/components/quote-matcher/similar-variant-cards';
import type { UomLadder } from 'pages/inquiry-processor/utils/price-units';
import { supplierPriceOf } from 'pages/inquiry-processor/utils/quotable';
import { useSupplierCatalogSuggestions } from 'pages/inquiry-processor/utils/use-supplier-catalog-suggestions';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface ProductMatcherProps {
  supplierId: number | null | undefined;
  supplierName?: string;
  item: RestaurantInquiryItem; // its master product is the requested one; suggestions are scored against it
  value: QuotationSupplierProduct | null;
  // Staged "the supplier carries this" answer without a catalogue row: the
  // item's own master product, or a sibling variant (the quote names it on save).
  pendingCreate?: boolean;
  // Similar-variant staging: the label rides along for the staged row/badge.
  pendingVariantLabel?: string;
  onChange: (p: QuotationSupplierProduct | null) => void;
  // Absent in the single-quote modals (add/edit) — they pick from suggestions
  // and search only; the staging CTA lives in the quote workbench. A variant
  // argument means the supplier carries a SIBLING variant of the item's master
  // product, picked from the family list.
  onStageCreate?: (variant?: StagedVariant) => void;
  // Master products this supplier ALREADY quoted on the item (live quotes) —
  // the multi-alternative flow re-opens the matcher on a quoted item, and these
  // must not be pickable again (the create endpoint 409s per supplier + master).
  quotedMasterProductIds?: number[];
}

export interface StagedVariant {
  id: number; // masterProductId of the sibling variant
  label: string;
  // The sibling's OWN packaging ladder — the item's master describes a
  // different variant, so the staged line's rung choices must come from here.
  ladder?: UomLadder;
}

// A family's variants share the name (the family's universalName) — the
// distinguishing attributes ARE the label.
const variantLabel = (v: MasterProduct): string =>
  [v.brand, v.origin, v.state, v.spec, v.baseUnit].filter(Boolean).join(' · ') || `Variant #${v.id}`;

const productMeta = (p: QuotationSupplierProduct) =>
  [p.brandEn, p.defaultUnit, p.origin, p.state].filter(Boolean).join(' · ');

// Two-line option: name+brand on top, price line below. Gated on the menu
// context so the closed control shows only the plain label (send-order-modal
// pattern).
const formatProductOption = (option: any, meta: { context: string }) => {
  if (meta.context !== 'menu' || !option?.raw) return option?.label;
  const p: QuotationSupplierProduct = option.raw;
  const metaLine = productMeta(p);
  return (
    <Flex direction='column' gap='0.5'>
      <Text fontSize='sm'>{option.label}</Text>
      <Flex gap='2' align='center'>
        <ProductPriceTag product={p} />
        {metaLine && (
          <Text fontSize='xs' color='gray.400'>
            {metaLine}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

// A quote names a supplier and ANY master product (substitutes allowed), so:
//   1. The requested master product auto-selects when the supplier prices it.
//   2. Otherwise "Does {supplier} carry this?" quotes the requested product, or
//      a similar variant of its family.
//   3. Or the operator picks a substitute from the suggestions or the search.
// Selecting collapses everything to a compact row with a Change action.
export const ProductMatcher = ({
  supplierId,
  supplierName,
  item,
  value,
  pendingCreate,
  pendingVariantLabel,
  onChange,
  onStageCreate,
  quotedMasterProductIds,
}: ProductMatcherProps) => {
  const { loadOptions } = useSearchQuotationSupplierProductsApi();
  const { suggestions, isLoading } = useSupplierCatalogSuggestions(
    value || pendingCreate ? null : supplierId, // no catalog churn while collapsed
    item
  );
  // "A similar variant" panel — lazily lists the master family's variants.
  const [showVariants, setShowVariants] = useState(false);
  // One decision surface at a time: while the variant panel is open, the
  // suggestions + catalog search collapse behind a "More options" disclosure.
  const [showOther, setShowOther] = useState(false);
  const [newVariantOpen, setNewVariantOpen] = useState(false);
  const familyId = item.masterProduct?.familyId ?? undefined;
  const variantsQuery = useGetMasterProductsApi(familyId, { enabled: showVariants && !!onStageCreate });
  // Unscoped, display-only: the cards LABEL attribute values the variants
  // already carry (same rationale as the wizard picker's Attributes column).
  const { data: dictionaryData } = useGetAttributeDictionaryApi();
  const dictionary = dictionaryData?.data;
  const siblingVariants = (variantsQuery.data?.data ?? []).filter((v) => v.id !== item.masterProductId);

  const masterName = item.masterProduct?.name || `Item #${item.id}`;
  const isLinkedToItem = (p: QuotationSupplierProduct) =>
    p.matchSource === 'LINKED' || (p.masterProductId ?? p.id) === item.masterProductId;
  const isQuoted = (p: QuotationSupplierProduct) => !!quotedMasterProductIds?.includes(p.masterProductId ?? p.id);

  // Auto-select the requested master product when the supplier prices it (it
  // "carries" it) — once per supplier/item, so "Change" doesn't immediately snap
  // the selection back. An already-quoted one never auto-selects: the operator
  // came back for a DIFFERENT product.
  const autoSelectedRef = useRef<string | null>(null);
  const linked = suggestions.find(({ product }) => isLinkedToItem(product))?.product;
  const linkedQuoted = !!linked && isQuoted(linked);
  const linkedPriced = !!linked && supplierPriceOf(linked) != null;
  useEffect(() => {
    const key = `${supplierId}:${item.id}`;
    if (!value && !pendingCreate && linked && linkedPriced && !linkedQuoted && autoSelectedRef.current !== key) {
      autoSelectedRef.current = key;
      onChange(linked);
    }
  }, [linked, linkedPriced, linkedQuoted, value, pendingCreate, supplierId, item.id, onChange]);

  // While "Yes — quote it" offers the requested product, its card would repeat it.
  const cardSuggestions =
    onStageCreate && !linkedQuoted ? suggestions.filter(({ product }) => !isLinkedToItem(product)) : suggestions;

  const loadForSupplier = useMemo(() => {
    if (!supplierId) return undefined;
    const base = loadOptions(supplierId);
    if (!quotedMasterProductIds?.length) return base;
    // Quoted master products are dropped from the search rather than shown
    // disabled — an unpickable row in an async select reads as a bug, not a state.
    return (inputValue: string, callback: (options: QuotationSupplierProductOption[]) => void) =>
      base(inputValue, (options) => callback(options.filter((o) => !quotedMasterProductIds.includes(o.value))));
  }, [supplierId, loadOptions, quotedMasterProductIds]);
  const supplierLabel = supplierName || 'this supplier';

  if (!supplierId) {
    return (
      <Text fontSize='sm' color='gray.400'>
        Select a supplier first.
      </Text>
    );
  }

  if (value) {
    return (
      <Flex align='center' gap='3' border='1px solid' borderColor='gray.200' rounded='md' px='3' py='2' bg='white'>
        <Box flex='1' minW='0'>
          <Flex align='center' gap='2'>
            <Text fontSize='sm' fontWeight='semibold' noOfLines={1}>
              {value.nameEn || `Master product #${value.id}`}
            </Text>
            <Badge
              colorScheme={isLinkedToItem(value) ? 'purple' : 'orange'}
              variant='subtle'
              fontSize='2xs'
              flexShrink={0}>
              {isLinkedToItem(value) ? 'As requested' : 'Substitute'}
            </Badge>
          </Flex>
          <Text fontSize='xs' color='gray.500' noOfLines={1}>
            {productMeta(value) || '—'}
          </Text>
        </Box>
        <ProductPriceTag product={value} />
        <Button size='xs' variant='ghost' onClick={() => onChange(null)}>
          Change
        </Button>
      </Flex>
    );
  }

  if (pendingCreate) {
    return (
      <Flex
        align='center'
        gap='3'
        border='1px solid'
        borderColor='purple.200'
        rounded='md'
        px='3'
        py='2'
        bg='purple.50'>
        <Box flex='1' minW='0'>
          <Flex align='center' gap='2'>
            <Text fontSize='sm' fontWeight='semibold' noOfLines={1}>
              {masterName}
            </Text>
            <Badge colorScheme='purple' variant='subtle' fontSize='2xs' flexShrink={0}>
              {pendingVariantLabel ? 'Variant' : 'As requested'}
            </Badge>
          </Flex>
          {pendingVariantLabel && (
            <Text fontSize='xs' color='purple.700' noOfLines={1}>
              Variant: {pendingVariantLabel}
            </Text>
          )}
          <Text fontSize='xs' color='gray.600'>
            {supplierLabel} has no price for it yet: the quote’s price is the one you enter.
          </Text>
        </Box>
        <Button size='xs' variant='ghost' onClick={() => onChange(null)}>
          Change
        </Button>
      </Flex>
    );
  }

  return (
    <Box>
      {isLoading ? (
        <Flex align='center' gap='2' mb='2' color='gray.500' fontSize='sm'>
          <Spinner size='xs' /> Checking {supplierLabel}’s catalog…
        </Flex>
      ) : (
        <>
          {/* Primary path when the supplier has no linked product yet. */}
          {onStageCreate && (
            <Flex
              border='1px solid'
              borderColor='purple.200'
              bg='purple.50'
              rounded='md'
              p='3'
              mb='2'
              align='center'
              gap='3'>
              <Box flex='1' minW='0'>
                {linkedQuoted ? (
                  // One line only — the workbench card already carries the
                  // "Already quoted: SKU · price" context; repeating it here
                  // was the double-messaging the redesign removed.
                  <Text fontSize='sm' fontWeight='semibold'>
                    Add an alternative: a similar variant, or a substitute.
                  </Text>
                ) : (
                  <>
                    <Text fontSize='sm' fontWeight='semibold'>
                      Does {supplierLabel} carry “{masterName}”?
                    </Text>
                    <Text fontSize='xs' color='gray.600'>
                      Quotes the requested master product — nothing to look up.
                    </Text>
                  </>
                )}
              </Box>
              {!linkedQuoted && (
                <Button
                  size='sm'
                  colorScheme='red'
                  flexShrink={0}
                  onClick={() => (linked ? onChange(linked) : onStageCreate())}>
                  Yes — quote it
                </Button>
              )}
              {familyId && (
                <Button
                  size='sm'
                  variant='outline'
                  flexShrink={0}
                  onClick={() => {
                    setShowVariants((v) => !v);
                    setShowOther(false);
                  }}>
                  A similar variant…
                </Button>
              )}
            </Flex>
          )}

          {/* Similar-variant picker: the supplier carries this product, just a
              different variant of the family — pick which, or create it. */}
          {onStageCreate && showVariants && (
            <Box border='1px solid' borderColor='purple.100' rounded='md' p='2' mb='2'>
              <Text fontSize='10px' color='gray.500' textTransform='uppercase' letterSpacing='wide' mb='1'>
                Which “{masterName}” variant does {supplierLabel} carry?
              </Text>
              {variantsQuery.isLoading ? (
                <Flex align='center' gap='2' color='gray.500' fontSize='sm' py='1'>
                  <Spinner size='xs' /> Loading variants…
                </Flex>
              ) : siblingVariants.length ? (
                <SimilarVariantCards
                  variants={siblingVariants}
                  dictionary={dictionary}
                  // The variant's UOM fields ARE a ladder — passed flat, since
                  // ladderOf() reads the NESTED product.masterProduct shape.
                  onPick={(v) =>
                    onStageCreate({
                      id: v.id,
                      label: variantLabel(v),
                      ladder: {
                        baseUnit: v.baseUnit,
                        itemUnit: v.itemUnit,
                        netContent: v.netContent,
                        packUnit: v.packUnit,
                        packSize: v.packSize,
                      },
                    })
                  }
                  ask={{
                    brand: item.masterProduct?.brand,
                    origin: item.masterProduct?.origin,
                    state: item.masterProduct?.state,
                  }}
                />
              ) : (
                <Text fontSize='sm' color='gray.500' py='1'>
                  No other variants in this family yet.
                </Text>
              )}
              <Button size='xs' variant='link' colorScheme='red' mt='1' onClick={() => setNewVariantOpen(true)}>
                + The variant isn’t listed — create it
              </Button>
            </Box>
          )}

          {/* One decision surface at a time: with the variant panel open, the
              substitute paths collapse behind a disclosure instead of stacking a
              third and fourth block under it. */}
          {onStageCreate && showVariants && (
            <Button
              size='xs'
              variant='link'
              color='gray.600'
              fontWeight='600'
              mb='2'
              onClick={() => setShowOther((o) => !o)}>
              {showOther ? 'Hide other options' : 'More options — substitutes & search'}
            </Button>
          )}
          {(!onStageCreate || !showVariants || showOther) && (
            <>
              {cardSuggestions.length > 0 && (
                <>
                  <Flex align='baseline' gap='2' mb='1'>
                    <Text fontSize='10px' color='gray.500' textTransform='uppercase' letterSpacing='wide'>
                      Possible matches
                    </Text>
                    <Text fontSize='10px' color='gray.400'>
                      Master products by name, the ones {supplierLabel} prices first
                    </Text>
                  </Flex>
                  {/* Up to 6 cards, 3 per row (2 rows max — the hooks cap at 6). */}
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(3, minmax(0, 1fr))' }} gap='2' mb='2'>
                    {cardSuggestions.map(({ product, matchScore }) => (
                      <Box
                        key={product.id}
                        as='button'
                        type='button'
                        disabled={isQuoted(product)}
                        onClick={() => onChange(product)}
                        textAlign='left'
                        border='1px solid'
                        borderColor='gray.200'
                        rounded='md'
                        px='3'
                        py='2'
                        bg='white'
                        opacity={isQuoted(product) ? 0.55 : 1}
                        cursor={isQuoted(product) ? 'not-allowed' : 'pointer'}
                        _hover={isQuoted(product) ? undefined : { borderColor: 'red.300', boxShadow: 'sm' }}>
                        <Flex justify='space-between' align='flex-start' gap='1' mb='0.5'>
                          <Text fontSize='sm' fontWeight='semibold' noOfLines={2}>
                            {product.nameEn || `Master product #${product.id}`}
                          </Text>
                          {isQuoted(product) ? (
                            <Badge colorScheme='gray' variant='subtle' fontSize='2xs' flexShrink={0} mt='0.5'>
                              Quoted
                            </Badge>
                          ) : (
                            <Badge colorScheme='green' variant='subtle' fontSize='2xs' flexShrink={0} mt='0.5'>
                              {matchScore}%
                            </Badge>
                          )}
                        </Flex>
                        <Text fontSize='xs' color='gray.500' noOfLines={1} mb='1'>
                          {productMeta(product) || '—'}
                        </Text>
                        <ProductPriceTag product={product} />
                      </Box>
                    ))}
                  </Grid>
                </>
              )}
              <ChakraAsyncSelect
                loadOptions={loadForSupplier}
                defaultOptions
                value={null}
                onChange={(opt: any) => opt?.raw && onChange(opt.raw)}
                placeholder='Search master products to quote (name or brand)…'
                formatOptionLabel={formatProductOption}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
              />
            </>
          )}
        </>
      )}

      {/* Inline master-variant creation — the same form the families screen
          uses; its create hook invalidates the variants list, so the new
          variant appears in the picker on close, ready to pick. */}
      {newVariantOpen && familyId && (
        <VariantFormModal
          isOpen
          onClose={() => setNewVariantOpen(false)}
          familyId={familyId}
          familyImageUrl={item.masterProduct?.imageUrl}
          familyCategoryId={item.masterProduct?.catalogCategoryId ?? undefined}
          // Seed from the requested variant: the supplier's variant almost
          // always differs in exactly one field, so the operator edits that
          // one instead of filling the form from scratch.
          seed={{
            name: item.masterProduct?.name,
            brand: item.masterProduct?.brand,
            origin: item.masterProduct?.origin,
            state: item.masterProduct?.state,
            baseUnit: item.masterProduct?.baseUnit,
            itemUnit: item.masterProduct?.itemUnit,
            netContent: item.masterProduct?.netContent,
            packUnit: item.masterProduct?.packUnit,
            packSize: item.masterProduct?.packSize,
          }}
        />
      )}
    </Box>
  );
};
